import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { ReactNode } from "react";

import { cn } from "../utls/utils";
import Typography from "./typography";

type ErrorDisplayTone = "danger" | "warning" | "neutral";

export interface ErrorDisplayProps {
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  tone?: ErrorDisplayTone;
  compact?: boolean;
  className?: string;
}

const toneStyles: Record<ErrorDisplayTone, Record<string, string>> = {
  danger: {
    container: "border-danger/40 bg-danger/10 text-danger",
    iconWrapper: "bg-danger/20 text-danger",
    title: "text-danger",
    description: "text-danger/90",
  },
  warning: {
    container: "border-terracotta-200 bg-terracotta-50 text-terracotta-700",
    iconWrapper: "bg-terracotta-100 text-terracotta-600",
    title: "text-terracotta-700",
    description: "text-terracotta-700",
  },
  neutral: {
    container: "border-brown-200 bg-cream-100 text-brown-700",
    iconWrapper: "bg-beige-200 text-brown-700",
    title: "text-brown-800",
    description: "text-brown-700",
  },
};

export function ErrorDisplay({
  title = "We ran into a problem",
  description,
  children,
  icon,
  actions,
  tone = "danger",
  compact = false,
  className,
}: ErrorDisplayProps) {
  const resolvedDescription = description ?? children;
  const isStringDescription = typeof resolvedDescription === "string";
  const styles = toneStyles[tone];

  return (
    <div
      role="alert"
      className={cn(
        "w-full rounded-lg border",
        compact ? "px-3 py-2" : "px-4 py-3",
        styles.container,
        className
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
            styles.iconWrapper
          )}
        >
          {icon ?? (
            <ExclamationTriangleIcon className="h-5 w-5" aria-hidden />
          )}
        </span>
        <div className="flex-1 space-y-2">
          {title ? (
            typeof title === "string" ? (
              <Typography
                variant="subtitle1"
                className={cn("font-semibold", styles.title)}
              >
                {title}
              </Typography>
            ) : (
              title
            )
          ) : null}

          {resolvedDescription ? (
            isStringDescription ? (
              <Typography variant="body2" className={styles.description}>
                {resolvedDescription as string}
              </Typography>
            ) : (
              resolvedDescription
            )
          ) : null}

          {actions ? (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {actions}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default ErrorDisplay;
